import { Modal } from '@pmi/dsm-react';
import { ReactNode, useState } from 'react';
import { useSelector } from 'react-redux';
import { toastWithAnalytics } from '../../../../../shared/analytics';
import {
  getResponseErrorMessage,
  getUserText,
} from '../../../../../shared/helpers';
import { User } from '../../../../../shared/models/UserModels';
import { accountSelector } from '../../../../../shared/store/shared/accountSlice';
import { useDisableUserAccountMutation } from './apiSlice';
import './styles.module.scss';

interface ForbidRestoreUserModalProps {
  user: User;
  onCancel?: () => void;
  onSuccess?: () => void;
}

export default function DisableUserModal({
  user,
  onSuccess = () => {},
  onCancel = () => {},
}: ForbidRestoreUserModalProps) {
  const { id } = user;
  const { currentAccountId } = useSelector(accountSelector);
  const [isProcessing, setIsProcessing] = useState(false);

  // mutations
  const [disableUser, disabledUser] = useDisableUserAccountMutation();

  const onCancelHandler = () => {
    onCancel();
  };

  const successMessage = (): ReactNode => (
    <span data-exclude-lg="true">You have disabled {getUserText(user)}.</span>
  );
  const errorMessage = (err: any): ReactNode => {
    const message = getResponseErrorMessage(err);
    if (message) {
      return (
        <span className="fw-normal">
          {message
            .replace('remove', 'disable')
            .replace('removing', 'disabling')}
        </span>
      );
    }
    return (
      <span className="fw-normal">
        There was an error disabling{' '}
        <span data-exclude-lg="true">{getUserText(user)}</span>. Please try
        again.
      </span>
    );
  };
  const description =
    'Disabling this user will temporarily revoke their access to these products until their account is re-enabled.';
  const question = 'Are you sure you want to disable';

  const onRemove = () => {
    setIsProcessing(true);
    disableUser({ accountId: currentAccountId, userId: id })
      .unwrap()
      .then(() => {
        toastWithAnalytics(successMessage(), 'success');
        disabledUser.reset();
        onSuccess();
        onCancel();
      })
      .catch((err) => {
        toastWithAnalytics(errorMessage(err), 'danger');
        setIsProcessing(false);
      })
      .finally(() => {
        setIsProcessing(false);
      });
  };

  return (
    <Modal
      show
      onClose={onCancelHandler}
      headline={`${question} ${getUserText(user)}?`}
      description={description}
      customClassName="forbidRestoreUser"
      buttons={[
        {
          titleText: 'Cancel',
          variant: 'outline-secondary',
          onClick: onCancelHandler,
          disabled: isProcessing,
        },
        {
          titleText: 'Disable user',
          variant: 'primary',
          onClick: onRemove,
          disabled: isProcessing,
        },
      ]}
    />
  );
}
